import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { useAuth } from '../context/AuthContext';
import type { JourneyStep } from '../types';

function StepIcon({ step }: { step: JourneyStep }) {
  const isConversion = step.type === 'event' && ['phone_click', 'whatsapp_click', 'contact_form_submit'].includes(step.eventName);
  const color = step.type === 'page_view' ? 'bg-brand-500' : isConversion ? 'bg-emerald-500' : 'bg-slate-400';
  return <div className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${color}`} />;
}

function StepContent({ step }: { step: JourneyStep }) {
  if (step.type === 'page_view') {
    return (
      <div>
        <p className="font-medium text-slate-900 dark:text-white">{step.title || step.path}</p>
        <p className="font-mono text-xs text-slate-500 dark:text-slate-400">{step.path}</p>
        <p className="mt-0.5 text-xs text-slate-400">
          {step.timeOnPageSeconds}s auf Seite · {step.maxScrollDepth}% gescrollt
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="font-medium text-slate-900 dark:text-white">{step.eventName}</p>
      {step.payload != null && Object.keys(step.payload as object).length > 0 && (
        <pre className="mt-1 rounded bg-slate-100 px-2 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {JSON.stringify(step.payload)}
        </pre>
      )}
    </div>
  );
}

export function SessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { currentSiteId } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['session-detail', currentSiteId, sessionId],
    queryFn: () => api.sessionDetail(currentSiteId!, Number(sessionId)),
    enabled: !!currentSiteId && !!sessionId,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/sessions" className="text-sm text-slate-500 hover:underline dark:text-slate-400">
          ← Sessions
        </Link>
      </div>
      <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Session #{sessionId}</h1>

      <Card title="User Journey">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : !data || data.steps.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">Keine Schritte aufgezeichnet.</p>
        ) : (
          <ol className="space-y-5">
            {data.steps.map((step, i) => (
              <li key={i} className="flex gap-3">
                <StepIcon step={step} />
                <div className="flex-1">
                  <p className="text-xs text-slate-400">{new Date(step.at).toLocaleTimeString('de-DE')}</p>
                  <StepContent step={step} />
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}
